import {
  Component,
  DestroyRef,
  OnInit,
  OutputEmitterRef,
  Signal,
  ViewEncapsulation,
  WritableSignal,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TranslateDirective } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';

import { Address } from '../../domain/address.domain';
import { AddressService } from '../../features/address/address.service';
import { PostalCodeService } from '../../features/postal-code/postal-code.service';
import { CallOutComponent } from '../../features/call-out/call-out.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ProvincesStatesCodesService } from '../../services/provinces-states/provinces-states-codes.service';
import { RuralRoutePoBoxInputDirective } from '../input/rural-route-po-box-input.directive';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { MaterialAddressValidator } from './material-address.validator';

/** The nine editable fields. `streetNumDisplay` is the combined number+suffix legacy shows. */
interface MaterialAddressForm {
  streetNumDisplay: FormControl<string>;
  streetName: FormControl<string>;
  unitNum: FormControl<string>;
  cityName: FormControl<string>;
  territory: FormControl<string>;
  postalCode: FormControl<string>;
  ruralOrPoBoxType: FormControl<string>;
  ruralOrPoBox: FormControl<string>;
  postalStationTxt: FormControl<string>;
}

@Component({
  selector: 'td-wb-material-address',
  templateUrl: './material-address.component.html',
  styleUrls: ['./material-address.component.scss', '../../features/address/address-autocomplete.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'td-wb-material-address'
  },
  providers: [
    AddressService,
    MaterialAddressValidator,
    { provide: NG_VALIDATORS, useExisting: MaterialAddressValidator, multi: true },
    ValueAccessorProviderFactory(MaterialAddressComponent)
  ],
  imports: [
    ReactiveFormsModule,
    TranslateDirective,
    TranslatePipe,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    RuralRoutePoBoxInputDirective,
    CallOutComponent
  ]
})
export class MaterialAddressComponent implements ControlValueAccessor, OnInit, Validator {
  private readonly addressService = inject(AddressService);
  private readonly provincesStatesCodes = inject(ProvincesStatesCodesService);
  private readonly addressValidator = inject(MaterialAddressValidator);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _value: WritableSignal<Address | null> = signal<Address | null>(null);
  private readonly _disabledByCva: WritableSignal<boolean> = signal(false);
  private onChangeCallback: (value: Address | null) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  get value(): Address | null {
    return this._value();
  }

  get disabled(): boolean {
    return this._disabledByCva();
  }

  /** Suffix for every `td-wb-address-*` id. AddressComplete resolves its bindings by literal id. */
  readonly usageId = input<string>('home');
  readonly showTitleAndCallout = input<boolean>(true);
  readonly showRuralRouteAndPOBox = input<boolean>(true);

  private passThrough: Address = undefined;

  /** Guards the service -> form path from re-entering through `valueChanges`. */
  private patching = false;

  private readonly ruralOrPoBoxValue = signal<string>('');
  protected readonly ruralOrPoBoxTypeValue = signal<string>('');
  private readonly territoryValue = signal<string>('');

  protected readonly form = new FormGroup<MaterialAddressForm>({
    streetNumDisplay: new FormControl<string>('', Validators.required),
    streetName: new FormControl<string>('', Validators.required),
    unitNum: new FormControl<string>(''),
    cityName: new FormControl<string>('', Validators.required),
    territory: new FormControl<string>('', Validators.required),
    postalCode: new FormControl<string>('', [
      Validators.required,
      (control: AbstractControl): ValidationErrors | null => this.validatePostalCode(control)
    ]),
    ruralOrPoBoxType: new FormControl<string>(''),
    ruralOrPoBox: new FormControl<string>(''),
    postalStationTxt: new FormControl<string>('')
  });

  protected readonly provinces: Array<{ code: string, name: string }> =
    Array.from(this.addressService.provinces, ([code, name]: [string, string]) => ({ code, name }));

  protected readonly ruralPoTypes: Array<string> = this.addressService.RURAL_PO_TYPES;

  protected readonly isHome: Signal<boolean> = computed((): boolean => this.usageId() === 'home');

  private readonly isRuralOrPoBoxEmpty: Signal<boolean> = computed((): boolean => {
    const value = this.ruralOrPoBoxValue();
    return value === '' || value === null || value === undefined;
  });

  /** Enabled for `home` always, for `mailing` only once a type is chosen, never otherwise. */
  private readonly disablePOBoxRuralRoute: Signal<boolean> = computed((): boolean => {
    const usageId = this.usageId();
    const hasType = this.ruralPoTypes.includes(this.ruralOrPoBoxTypeValue());
    return !(usageId === 'home' || (usageId === 'mailing' && hasType));
  });

  private readonly isPoBoxRuralRouteRequired: Signal<boolean> = computed((): boolean =>
    !this.isHome() && this.ruralPoTypes.includes(this.ruralOrPoBoxTypeValue())
  );

  private readonly shouldDisableStation: Signal<boolean> = computed((): boolean =>
    this.disablePOBoxRuralRoute() || this.isRuralOrPoBoxEmpty()
  );

  private readonly isStreetRequired: Signal<boolean> = computed((): boolean =>
    !this.showRuralRouteAndPOBox() || this.isRuralOrPoBoxEmpty()
  );

  protected readonly ruralOrPoBoxLabel: Signal<string> = computed((): string =>
    this.isHome() ? 'tdWb.address.RURAL_ROUTE_OPTIONAL' : 'tdWb.address.PO_BOX_NUM'
  );

  protected readonly titleKey: Signal<string> = computed((): string =>
    this.isHome() ? 'tdWb.address.NEW_HOME_ADDRESS' : 'tdWb.address.MAILING_ADDRESS'
  );

  constructor() {
    this.addressValidator.validateFn = (): ValidationErrors | null => this.validate();
  }

  ngOnInit(): void {
    this.addressService.loadAddressComplete(this.usageId());
    this.addressService.onSelectedAddressChange
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((address: Address): void => this.applyAddress(address));

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.onFormChange());

    this.syncControlState();
  }

  writeValue(value: Address): void {
    this._value.set(value ?? null);
    this.applyAddress(value);
  }

  registerOnChange(fn: (value: Address | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.syncControlState();

    this.addressValidator.revalidate();
  }

  validate(): ValidationErrors | null {
    if (this._disabledByCva()) {
      return null;
    }

    return this.form.valid ? null : { address: true };
  }

  private applyAddress(address: Address): void {
    if (address === undefined || address === null) {
      return;
    }

    this.passThrough = { ...address };
    this.patching = true;

    const streetNumDisplay: string = [address.streetNum, address.streetNumSuffixVal].join('');

    const showBlock: boolean = this.showRuralRouteAndPOBox();
    let ruralOrPoBox = '';
    let ruralOrPoBoxType = '';

    if (address.ruralRouteNum && showBlock) {
      ruralOrPoBox = address.ruralRouteNum;
      ruralOrPoBoxType = AddressService.RURAL_ROUTE_TYPE;
    } else if (address.poBox && showBlock) {
      ruralOrPoBox = address.poBox;
      ruralOrPoBoxType = AddressService.PO_BOX_TYPE;
    }

    this.form.patchValue({
      streetNumDisplay,
      streetName: address.streetName ?? '',
      unitNum: address.unitNum ?? '',
      cityName: address.cityName ?? '',
      territory: address.territory ?? '',
      postalCode: address.postalCode ?? '',
      ruralOrPoBoxType,
      ruralOrPoBox,
      postalStationTxt: address.postalStationTxt ?? ''
    }, { emitEvent: false });

    this.ruralOrPoBoxValue.set(ruralOrPoBox);
    this.ruralOrPoBoxTypeValue.set(ruralOrPoBoxType);
    this.territoryValue.set(address.territory ?? '');
    this.syncControlState();

    this.patching = false;
    this.emitValue();
  }

  private onFormChange(): void {
    if (this.patching) {
      return;
    }

    this.ruralOrPoBoxValue.set(this.form.controls.ruralOrPoBox.value ?? '');
    this.ruralOrPoBoxTypeValue.set(this.form.controls.ruralOrPoBoxType.value ?? '');
    this.territoryValue.set(this.form.controls.territory.value ?? '');

    if (this.isRuralOrPoBoxEmpty() && this.form.controls.postalStationTxt.value !== '') {
      this.form.controls.postalStationTxt.setValue('', { emitEvent: false });
    }

    this.syncControlState();
    this.emitValue();

    this.onTouchedCallback();
    this.touch.emit();
  }

  private emitValue(): void {
    const address: Address = this.buildAddress();
    this._value.set(address);
    this.onChangeCallback(address);
  }

  private buildAddress(): Address {
    const value = this.form.getRawValue();
    const base: Address = { ...(this.passThrough ?? {} as Address) };

    base.streetNumSuffixVal = this.addressService.parseStreetNumberSuffix(value.streetNumDisplay);
    base.streetNum = this.addressService.parseStreetNumber(value.streetNumDisplay);
    base.streetName = value.streetName;
    base.unitNum = value.unitNum;
    base.cityName = value.cityName;
    base.postalCode = value.postalCode;
    base.territory = value.territory;

    if (this.showRuralRouteAndPOBox()) {
      const type: string = this.isHome() && value.ruralOrPoBox
        ? AddressService.RURAL_ROUTE_TYPE
        : value.ruralOrPoBoxType;

      base.ruralRouteTypeCd = type;
      if (type === AddressService.PO_BOX_TYPE) {
        base.poBox = value.ruralOrPoBox;
        base.ruralRouteNum = '';
      } else {
        base.ruralRouteNum = value.ruralOrPoBox;
        base.poBox = '';
      }
      base.postalStationTxt = value.postalStationTxt;
    }

    base.territoryCd = base.territory;
    base.countryCd = base.countryCd ? base.countryCd : 'CA';
    base.country = base.country ? base.country : 'Canada';

    return base;
  }

  private syncControlState(): void {
    this.syncValidators();
    this.syncDisabledState();
  }

  private syncValidators(): void {
    const streetRequired: boolean = this.isStreetRequired();

    this.setRequired(this.form.controls.streetNumDisplay, streetRequired);
    this.setRequired(this.form.controls.streetName, streetRequired);
    this.setRequired(this.form.controls.ruralOrPoBox, this.isPoBoxRuralRouteRequired());

    this.form.controls.postalCode.updateValueAndValidity({ emitEvent: false });
  }

  private setRequired(control: FormControl<string>, required: boolean): void {
    if (required === control.hasValidator(Validators.required)) {
      return;
    }

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private validatePostalCode(control: AbstractControl): ValidationErrors | null {
    const province: string = this.territoryValue();
    const provinceCodes: Record<string, string> = this.provincesStatesCodes.provinces?.provinceCodes ?? {};
    const isKnownProvince: boolean = Object.values(provinceCodes).includes(province);

    let valid: boolean;
    if (province === this.provincesStatesCodes.other?.otherCountry) {
      valid = PostalCodeService.OTHER_POSTAL_CODE_REGEX.test(control.value);
    } else if (isKnownProvince) {
      valid = PostalCodeService.CANADIAN_POSTAL_CODE_REGEX.test(control.value);
    } else {
      valid = PostalCodeService.US_POSTAL_CODE_REGEX.test(control.value);
    }

    return valid ? null : { invalidPostalCode: true };
  }

  private syncDisabledState(): void {
    const cvaDisabled: boolean = this._disabledByCva();
    const controls = this.form.controls;

    this.setEnabled(controls.streetNumDisplay, !cvaDisabled);
    this.setEnabled(controls.streetName, !cvaDisabled);
    this.setEnabled(controls.unitNum, !cvaDisabled);
    this.setEnabled(controls.cityName, !cvaDisabled);
    this.setEnabled(controls.territory, !cvaDisabled);
    this.setEnabled(controls.postalCode, !cvaDisabled);
    this.setEnabled(controls.ruralOrPoBoxType, !cvaDisabled);
    this.setEnabled(controls.ruralOrPoBox, !cvaDisabled && !this.disablePOBoxRuralRoute());
    this.setEnabled(controls.postalStationTxt, !cvaDisabled && !this.shouldDisableStation());
  }

  private setEnabled(control: FormControl<string>, enabled: boolean): void {
    if (enabled && control.disabled) {
      control.enable({ emitEvent: false });
    } else if (!enabled && control.enabled) {
      control.disable({ emitEvent: false });
    }
  }
}

